import {ref, Ref} from 'vue';
import {Client, IMessage} from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import NamaURLS from '@/utils/constants/NamaURLS';
import LoginUtils from '@/utils/login/LoginUtils';
import {useErrorsStore} from '@/stores/ToastDataStore';
import {useLoginInfoStore} from '@/stores/loginInfo';
import {namaBroadcast} from './NamaBroadcastChannel';
import {
  MessageMetadata,
  MessageStatusUpdate,
  PresenceUpdate,
  TaskProgressPayload,
  WsChatUnreadPayload,
  WsCountsPayload,
  WsNotificationsAndApprovalsPayload,
  WsReceiptPayload,
  WsSendMessagePayload,
  WsSendTypingPayload,
  WsSubscribeTypingPayload,
  WsTasksPayload
} from '@/utils/presenters/interfaces';
import {BroadcastMessageType, WebSocketMessageType} from "@/utils/UIConstants/enums.ts";
import NotificationUtils from "@/utils/broadcast/NotificationUtils.ts";
import TypingIndicatorDTO = namaTypes.TypingIndicatorDTO;
import DTOChatMessage = namaTypes.DTOChatMessage;
import DTOLargeData = namaTypes.DTOLargeData;

type WebSocketHandler<T = any> = (payload: T) => void;

/**
 * Singleton WebSocket manager that handles STOMP connection
 * Only the leader tab maintains the actual WebSocket connection
 * Other tabs send/receive messages via BroadcastChannel
 */
class NamaWebSocketManager {
  private static instance: NamaWebSocketManager;

  // WebSocket state
  private stompClient: Client | null = null;
  private subscriptions: Map<string, any> = new Map();
  connected = ref(false);
  isConnecting = ref(false);
  private initialized = false;

  // Event handlers
  private handlers: Map<WebSocketMessageType, Set<WebSocketHandler>> = new Map();

  // Broadcast unsubscribers
  private broadcastUnsubscribers: (() => void)[] = [];

  // Current user
  private get currentUserId(): string | undefined {
    return LoginUtils.currentUserInfo()?.id;
  }

  private constructor() {
  }

  static getInstance(): NamaWebSocketManager {
    if (!NamaWebSocketManager.instance) {
      NamaWebSocketManager.instance = new NamaWebSocketManager();
    }
    return NamaWebSocketManager.instance;
  }

  /**
   * Initialize WebSocket management
   * Should be called after login and after namaBroadcast.init()
   */
  init(): void {
    if (this.initialized) {
      console.warn('[WebSocket] Already initialized, skipping');
      return;
    }
    this.initialized = true;
    this.setupBroadcastListeners();
    // If this tab is leader, connect WebSocket
    if (namaBroadcast.isLeaderTab()) {
      this.connect();
    }
    // Listen for leader changes
    namaBroadcast.on(BroadcastMessageType.TAB_BECAME_LEADER, () => {
      if (namaBroadcast.isLeaderTab() && !this.connected.value) {
        this.connect();
      }
    });
  }

  /**
   * Setup listeners for broadcast messages from other tabs
   */
  private setupBroadcastListeners(): void {
    const messageTypes = [
      WebSocketMessageType.WS_CHAT_MESSAGE,
      WebSocketMessageType.WS_MESSAGE_STATUS,
      WebSocketMessageType.WS_PRESENCE_UPDATE,
      WebSocketMessageType.WS_TYPING_INDICATOR,
      WebSocketMessageType.WS_ERROR,
      WebSocketMessageType.WS_CONNECTED,
      WebSocketMessageType.WS_DISCONNECTED,
      WebSocketMessageType.WS_NOTIFICATIONS,
      WebSocketMessageType.WS_APPROVALS,
      WebSocketMessageType.WS_TASKS,
      WebSocketMessageType.WS_TASK_PROGRESS,
      WebSocketMessageType.WS_CHAT_UNREAD,
      WebSocketMessageType.WS_COUNTS,
    ];
    for (const type of messageTypes) {
      const unsub = namaBroadcast.on(type as unknown as BroadcastMessageType, async (payload) => {
        // Update local connection state
        if (type === WebSocketMessageType.WS_CONNECTED) {
          this.connected.value = true;
          this.isConnecting.value = false;
        } else if (type === WebSocketMessageType.WS_DISCONNECTED) {
          this.connected.value = false;
        }
        // Handle notification/approval updates in follower tabs
        if (!namaBroadcast.isLeaderTab()) {
          await this.handleNotificationBroadcast(type, payload);
        }
        // Notify local handlers
        this.notifyHandlers(type, payload);
      });
      this.broadcastUnsubscribers.push(unsub);
    }
    // Listen for outgoing requests from follower tabs (only leader processes these)
    const outgoingTypes = [
      WebSocketMessageType.WS_SEND_MESSAGE,
      WebSocketMessageType.WS_SEND_TYPING,
      WebSocketMessageType.WS_SEND_READ_RECEIPT,
      WebSocketMessageType.WS_SEND_DELIVERY_RECEIPT,
      WebSocketMessageType.WS_SUBSCRIBE_TYPING,
      WebSocketMessageType.WS_UNSUBSCRIBE_TYPING,
    ];
    for (const type of outgoingTypes) {
      const unsub = namaBroadcast.on(type as unknown as BroadcastMessageType, (payload) => {
        if (namaBroadcast.isLeaderTab()) {
          this.handleOutgoingRequest(type, payload);
        }
      });
      this.broadcastUnsubscribers.push(unsub);
    }
  }

  /**
   * Handle notification/approval broadcasts in follower tabs
   */
  private async handleNotificationBroadcast(type: WebSocketMessageType, payload: any) {
    const loginInfoStore = useLoginInfoStore();
    switch (type) {
      case WebSocketMessageType.WS_NOTIFICATIONS: {
        await NotificationUtils.updateNotifications();
        break;
      }
      case WebSocketMessageType.WS_APPROVALS: {
        await NotificationUtils.updateApprovals();
        break;
      }
      case WebSocketMessageType.WS_TASKS: {
        const tasksPayload = payload as WsTasksPayload;
        loginInfoStore.updateTasks(tasksPayload.tasks, tasksPayload.count);
        break;
      }
      case WebSocketMessageType.WS_TASK_PROGRESS: {
        loginInfoStore.updateTasks(payload.results, payload.resultsCount);
        break;
      }
      case WebSocketMessageType.WS_CHAT_UNREAD: {
        const chatPayload = payload as WsChatUnreadPayload;
        loginInfoStore.updateChatMessages(chatPayload.count ?? 0);
        break;
      }
      case WebSocketMessageType.WS_COUNTS: {
        const countsPayload = payload as WsCountsPayload;
        loginInfoStore.notificationsCount = countsPayload.notificationsCount;
        loginInfoStore.approvalsCount = countsPayload.approvalsCount;
        break;
      }
    }
  }

  /**
   * Handle outgoing requests from follower tabs
   */
  private handleOutgoingRequest(type: WebSocketMessageType, payload: any): void {
    switch (type) {
      case WebSocketMessageType.WS_SEND_MESSAGE:
        this.doSendMessage(payload as WsSendMessagePayload);
        break;
      case WebSocketMessageType.WS_SEND_TYPING:
        this.doSendTyping(payload as WsSendTypingPayload);
        break;
      case WebSocketMessageType.WS_SEND_READ_RECEIPT:
        this.doSendReadReceipt(payload as WsReceiptPayload);
        break;
      case WebSocketMessageType.WS_SEND_DELIVERY_RECEIPT:
        this.doSendDeliveryReceipt(payload as WsReceiptPayload);
        break;
      case WebSocketMessageType.WS_SUBSCRIBE_TYPING:
        this.doSubscribeTyping(payload as WsSubscribeTypingPayload);
        break;
      case WebSocketMessageType.WS_UNSUBSCRIBE_TYPING:
        this.doUnsubscribeTyping(payload as WsSubscribeTypingPayload);
        break;
    }
  }

  /**
   * Connect to WebSocket server (only called by leader tab)
   */
  private connect(): void {
    const userId = this.currentUserId;
    if (!userId) {
      console.warn('[WebSocket] No user ID, cannot connect');
      return;
    }
    if (this.connected.value || this.isConnecting.value) {
      return;
    }
    this.isConnecting.value = true;
    const socket = new SockJS(NamaURLS.WS_CHAT);
    socket.onclose = (event) => {
      console.error('[WebSocket] SockJS connection closed:', event);
    };
    this.stompClient = new Client({
      webSocketFactory: () => socket,
      reconnectDelay: 5000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      connectHeaders: {
        userId: userId,
      },
      onConnect: () => {
        this.connected.value = true;
        this.isConnecting.value = false;
        console.log('[WebSocket] Connected as user:', userId);
        // Subscribe to user-specific queues
        this.subscribeToUserQueues(userId);
        // Broadcast connection status to other tabs
        this.broadcastToAllTabs(WebSocketMessageType.WS_CONNECTED, {userId});
      },
      onStompError: (frame) => {
        console.error('[WebSocket] STOMP error:', frame);
        this.connected.value = false;
        this.isConnecting.value = false;
        this.broadcastToAllTabs(WebSocketMessageType.WS_DISCONNECTED, {});
      },
      onWebSocketClose: (event) => {
        console.error('[WebSocket] WebSocket closed:', event);
        this.connected.value = false;
        this.isConnecting.value = false;
        this.broadcastToAllTabs(WebSocketMessageType.WS_DISCONNECTED, {});
      },
      onWebSocketError: (event) => {
        console.error('[WebSocket] WebSocket error:', event);
        this.connected.value = false;
        this.isConnecting.value = false;
      },
    });
    this.stompClient.activate();
  }

  /**
   * Subscribe to user-specific STOMP queues
   */
  private subscribeToUserQueues(userId: string): void {
    // Chat messages
    this.safeSubscribe(`/queue/messages-${userId}`, (msg: IMessage) => {
      const message: MessageMetadata = JSON.parse(msg.body);
      this.broadcastToAllTabs(WebSocketMessageType.WS_CHAT_MESSAGE, {message});
      this.notifyHandlers(WebSocketMessageType.WS_CHAT_MESSAGE, {message});
    });
    // Message status updates
    this.safeSubscribe(`/queue/message-status-${userId}`, (msg: IMessage) => {
      const statusUpdate: MessageStatusUpdate = JSON.parse(msg.body);
      this.broadcastToAllTabs(WebSocketMessageType.WS_MESSAGE_STATUS, {statusUpdate});
      this.notifyHandlers(WebSocketMessageType.WS_MESSAGE_STATUS, {statusUpdate});
    });
    // Presence updates (global topic)
    this.safeSubscribe('/topic/user.presence', (msg: IMessage) => {
      const presenceUpdate: PresenceUpdate = JSON.parse(msg.body);
      this.broadcastToAllTabs(WebSocketMessageType.WS_PRESENCE_UPDATE, {presenceUpdate});
      this.notifyHandlers(WebSocketMessageType.WS_PRESENCE_UPDATE, {presenceUpdate});
    });
    // Errors
    this.safeSubscribe(`/queue/errors-${userId}`, (msg: IMessage) => {
      const error = JSON.parse(msg.body);
      console.error('[WebSocket] Chat error:', error);
      this.broadcastToAllTabs(WebSocketMessageType.WS_ERROR, error);
      this.notifyHandlers(WebSocketMessageType.WS_ERROR, error);
      useErrorsStore().pushErrorStr(error.message || 'Chat error occurred');
    });
    // Subscribe to notifications queue
    this.safeSubscribe(`/queue/notifications-${userId}`, async (msg: IMessage) => {
      const body: Record<string, any> | undefined = JSON.parse(msg.body);
      let playSound = body?.refresh ?? false;
      await NotificationUtils.updateNotifications(playSound);
      const loginInfoStore = useLoginInfoStore();
      const payload: WsNotificationsAndApprovalsPayload = {count: loginInfoStore.notificationsCount, result: loginInfoStore.notifications};
      this.broadcastToAllTabs(WebSocketMessageType.WS_NOTIFICATIONS, payload);
      this.notifyHandlers(WebSocketMessageType.WS_NOTIFICATIONS, payload);
    });
    // Subscribe to approvals queue
    this.safeSubscribe(`/queue/approvals-${userId}`, async (msg: IMessage) => {
      const body: Record<string, any> | undefined = JSON.parse(msg.body);
      let playSound = body?.refresh ?? false;
      await NotificationUtils.updateApprovals(playSound);
      const loginInfoStore = useLoginInfoStore();
      const payload: WsNotificationsAndApprovalsPayload = {count: loginInfoStore.approvalsCount, result: loginInfoStore.approvals};
      this.broadcastToAllTabs(WebSocketMessageType.WS_APPROVALS, payload);
      this.notifyHandlers(WebSocketMessageType.WS_APPROVALS, payload);
    });
    // Subscribe to counts queue (quick count updates without full data)
    this.safeSubscribe(`/queue/counts-${userId}`, async (msg: IMessage) => {
      await NotificationUtils.updateNotifications();
      await NotificationUtils.updateApprovals();
      const loginInfoStore = useLoginInfoStore();
      const payload: WsCountsPayload = {notificationsCount: loginInfoStore.notificationsCount, approvalsCount: loginInfoStore.approvalsCount};
      this.broadcastToAllTabs(WebSocketMessageType.WS_COUNTS, payload);
      this.notifyHandlers(WebSocketMessageType.WS_COUNTS, payload);
    });
    // TODO :: use one from updateTasks ??
    // Subscribe to tasks queue
    this.safeSubscribe(`/queue/tasks-${userId}`, (msg: IMessage) => {
      const payload: WsTasksPayload = JSON.parse(msg.body);
      useLoginInfoStore().updateTasks(payload.tasks, payload.count);
      // Broadcast to other tabs
      this.broadcastToAllTabs(WebSocketMessageType.WS_TASKS, payload);
      this.notifyHandlers(WebSocketMessageType.WS_TASKS, payload);
    });
    // Subscribe to task progress queue (high-frequency updates for running tasks)
    this.safeSubscribe(`/queue/task-progress-${userId}`, async (msg: IMessage) => {
      const payload: TaskProgressPayload = JSON.parse(msg.body);
      await NotificationUtils.updateTasks(payload);
      // Broadcast to other tabs
      this.broadcastToAllTabs(WebSocketMessageType.WS_TASK_PROGRESS, payload);
      this.notifyHandlers(WebSocketMessageType.WS_TASK_PROGRESS, payload);
    });
    // Subscribe to chat unread count queue
    this.safeSubscribe(`/queue/chat-unread-${userId}`, async (msg: IMessage) => {
      const payload: WsChatUnreadPayload = JSON.parse(msg.body);
      await NotificationUtils.updateChatMessages(payload)
      // Broadcast to other tabs
      this.broadcastToAllTabs(WebSocketMessageType.WS_CHAT_UNREAD, payload);
      this.notifyHandlers(WebSocketMessageType.WS_CHAT_UNREAD, payload);
    });
    // Subscribe to notifications by sending subscription message
    this.subscribeToNotificationService(userId);
  }

  /**
   * Send subscription message to notification service
   */
  private subscribeToNotificationService(userId: string): void {
    const employeeId = LoginUtils.currentUserEmployeeId();
    this.stompClient?.publish({
      destination: '/app/notifications.subscribe',
      body: JSON.stringify({
        userId: userId,
        employeeId: employeeId || ''
      })
    });
  }

  /**
   * Safe subscribe with duplicate prevention
   */
  private safeSubscribe(destination: string, callback: (msg: IMessage) => void): void {
    if (this.subscriptions.has(destination)) {
      try {
        this.subscriptions.get(destination)?.unsubscribe();
      } catch (e) {
        // Ignore
      }
      this.subscriptions.delete(destination);
    }
    const subscription = this.stompClient?.subscribe(destination, callback);
    if (subscription) {
      this.subscriptions.set(destination, subscription);
    }
  }

  /**
   * Broadcast message to all tabs via BroadcastChannel
   */
  private broadcastToAllTabs(type: WebSocketMessageType, payload: any): void {
    namaBroadcast.broadcast(type as unknown as BroadcastMessageType, payload);
  }

  /**
   * Notify local handlers
   */
  private notifyHandlers(type: WebSocketMessageType, payload: any): void {
    const handlers = this.handlers.get(type);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(payload);
        } catch (error) {
          console.error(`[WebSocket] Error in handler for ${type}:`, error);
        }
      });
    }
  }

  // ============ Public API for sending messages ============

  /**
   * Register a handler for WebSocket events
   */
  on<T>(type: WebSocketMessageType, handler: WebSocketHandler<T>): () => void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set());
    }
    this.handlers.get(type)!.add(handler as WebSocketHandler);
    return () => {
      this.handlers.get(type)?.delete(handler as WebSocketHandler);
    };
  }

  /**
   * Remove a handler
   */
  off<T>(type: WebSocketMessageType, handler: WebSocketHandler<T>): void {
    this.handlers.get(type)?.delete(handler as WebSocketHandler);
  }

  sendMessage(conversationId: string, messageContent: string, messageType: string = 'Text', attachment?: DTOLargeData, id?: string): void {
    const payload: WsSendMessagePayload = {conversationId, messageContent, messageType, attachment, id};
    if (namaBroadcast.isLeaderTab()) {
      this.doSendMessage(payload);
    } else {
      // Send request to leader tab
      namaBroadcast.broadcast(WebSocketMessageType.WS_SEND_MESSAGE as unknown as BroadcastMessageType, payload);
    }
  }

  private doSendMessage(payload: WsSendMessagePayload): void {
    if (!this.stompClient || !this.connected.value) {
      useErrorsStore().pushErrorStr('WebSocket not connected!');
      return;
    }
    const chatMessage: DTOChatMessage = {
      conversationId: payload.conversationId,
      sender: {
        id: this.currentUserId,
        entityType: LoginUtils.currentUserInfo()?.entityType
      },
      messageContent: payload.messageContent,
      messageType: payload.messageType || 'Text',
      attachment: payload.attachment,
      id: payload.id
    };
    this.stompClient.publish({
      destination: NamaURLS.CHAT_SEND,
      body: JSON.stringify(chatMessage)
    });
  }

  sendTyping(conversationId: string, typing: boolean): void {
    const payload: WsSendTypingPayload = {conversationId, typing};
    if (namaBroadcast.isLeaderTab()) {
      this.doSendTyping(payload);
    } else {
      namaBroadcast.broadcast(WebSocketMessageType.WS_SEND_TYPING as unknown as BroadcastMessageType, payload);
    }
  }

  private doSendTyping(payload: WsSendTypingPayload): void {
    if (!this.stompClient || !this.connected.value) return;
    this.stompClient.publish({
      destination: NamaURLS.CHAT_TYPING,
      body: JSON.stringify({
        userId: this.currentUserId,
        userType: LoginUtils.currentUserInfo()?.entityType,
        conversationId: payload.conversationId,
        typing: payload.typing
      })
    });
  }

  /**
   * Send read receipt
   */
  sendReadReceipt(messageId: string): void {
    const payload: WsReceiptPayload = {messageId};
    if (namaBroadcast.isLeaderTab()) {
      this.doSendReadReceipt(payload);
    } else {
      namaBroadcast.broadcast(WebSocketMessageType.WS_SEND_READ_RECEIPT as unknown as BroadcastMessageType, payload);
    }
  }

  private doSendReadReceipt(payload: WsReceiptPayload): void {
    if (!this.stompClient || !this.connected.value) return;
    this.stompClient.publish({
      destination: '/app/chat.read',
      body: payload.messageId
    });
  }

  /**
   * Send delivery receipt
   */
  sendDeliveryReceipt(messageId: string): void {
    const payload: WsReceiptPayload = {messageId};
    if (namaBroadcast.isLeaderTab()) {
      this.doSendDeliveryReceipt(payload);
    } else {
      namaBroadcast.broadcast(WebSocketMessageType.WS_SEND_DELIVERY_RECEIPT as unknown as BroadcastMessageType, payload);
    }
  }

  private doSendDeliveryReceipt(payload: WsReceiptPayload): void {
    if (!this.stompClient || !this.connected.value) return;
    this.stompClient.publish({
      destination: '/app/chat.delivered',
      body: payload.messageId
    });
  }

  /**
   * Subscribe to typing indicators for a conversation
   */
  subscribeToTyping(conversationId: string): void {
    const payload: WsSubscribeTypingPayload = {conversationId};
    if (namaBroadcast.isLeaderTab()) {
      this.doSubscribeTyping(payload);
    } else {
      namaBroadcast.broadcast(WebSocketMessageType.WS_SUBSCRIBE_TYPING as unknown as BroadcastMessageType, payload);
    }
  }

  private doSubscribeTyping(payload: WsSubscribeTypingPayload): void {
    const destination = `/topic/chat.typing.${payload.conversationId}`;
    this.safeSubscribe(destination, (msg: IMessage) => {
      const indicator: TypingIndicatorDTO = JSON.parse(msg.body);
      // Don't relay own typing indicators
      if (indicator.userId !== this.currentUserId) {
        this.broadcastToAllTabs(WebSocketMessageType.WS_TYPING_INDICATOR, {indicator});
        this.notifyHandlers(WebSocketMessageType.WS_TYPING_INDICATOR, {indicator});
      }
    });
  }

  /**
   * Unsubscribe from typing indicators
   */
  unsubscribeFromTyping(conversationId: string): void {
    const payload: WsSubscribeTypingPayload = {conversationId};
    if (namaBroadcast.isLeaderTab()) {
      this.doUnsubscribeTyping(payload);
    } else {
      namaBroadcast.broadcast(WebSocketMessageType.WS_UNSUBSCRIBE_TYPING as unknown as BroadcastMessageType, payload);
    }
  }

  private doUnsubscribeTyping(payload: WsSubscribeTypingPayload): void {
    const destination = `/topic/chat.typing.${payload.conversationId}`;
    if (this.subscriptions.has(destination)) {
      try {
        this.subscriptions.get(destination)?.unsubscribe();
      } catch (e) {
        // Ignore
      }
      this.subscriptions.delete(destination);
    }
  }

  /**
   * Request server to refresh notifications (e.g., after marking as read)
   */
  refreshNotifications(): void {
    const userId = this.currentUserId;
    if (!userId) return;
    if (namaBroadcast.isLeaderTab() && this.stompClient && this.connected.value) {
      this.stompClient.publish({
        destination: '/app/notifications.refresh',
        body: JSON.stringify({
          userId: userId,
          employeeId: LoginUtils.currentUserEmployeeId() || ''
        })
      });
    }
  }

  /**
   * Check if WebSocket is connected (checks leader tab status via broadcast)
   */
  isConnected(): boolean {
    return this.connected.value;
  }

  /**
   * Get reactive connection status
   */
  getConnectionStatus(): Ref<boolean> {
    return this.connected;
  }

  /**
   * Disconnect WebSocket and cleanup
   */
  async disconnect() {
    // Unsubscribe from all STOMP subscriptions
    this.subscriptions.forEach((subscription) => {
      try {
        subscription?.unsubscribe();
      } catch (e) {
        // Ignore
      }
    });
    this.subscriptions.clear();
    // Deactivate STOMP client
    if (this.stompClient) {
      await this.stompClient.deactivate();
      this.stompClient = null;
    }
    this.connected.value = false;
    this.isConnecting.value = false;
    // Broadcast disconnection to other tabs
    if (namaBroadcast.isLeaderTab()) {
      this.broadcastToAllTabs(WebSocketMessageType.WS_DISCONNECTED, {});
    }
    console.log('[WebSocket] Disconnected');
  }

  /**
   * Full cleanup - call on logout
   */
  async destroy() {
    await this.disconnect();
    // Cleanup broadcast listeners
    this.broadcastUnsubscribers.forEach(unsub => unsub());
    this.broadcastUnsubscribers = [];
    // Clear handlers
    this.handlers.clear();
    // Reset initialization flag to allow re-init after logout/login
    this.initialized = false;
    console.log('[WebSocket] Destroyed');
  }
}

// Export singleton instance
export const namaWebSocket = NamaWebSocketManager.getInstance();

